/**
 * Mapping entités Zustand → colonnes Supabase (snake_case).
 * Seules les lignes dont l’id est un UUID valide sont synchronisées.
 */

import { isUuid } from "./supabaseUuid";

/** Clone sérialisable pour colonne JSONB `payload` (round-trip DB). */
export function entityToPayload(entity) {
  if (!entity || typeof entity !== "object") return {};
  try {
    return JSON.parse(JSON.stringify(entity));
  } catch {
    return {};
  }
}

function splitClientName(name) {
  if (!name || typeof name !== "string") return { first: "Client", last: "" };
  const p = name.trim().split(/\s+/);
  if (p.length === 1) return { first: p[0].slice(0, 80), last: "" };
  return { first: p[0].slice(0, 80), last: p.slice(1).join(" ").slice(0, 80) };
}

function normalizeClientStatus(s) {
  if (s === "archived" || s === "standby" || s === "active") return s;
  // Legacy: "ending" n'existe plus depuis la refonte billing v4 (pause=gel, archive=suppression).
  // On mappe vers "archived" car un client ending était en cours d'archivage.
  if (s === "ending") return "archived";
  return "active";
}

/**
 * @returns {object|null} ligne `clients` ou null si id non UUID
 */
export function clientAppToDbRow(client, coachId) {
  if (!client || !isUuid(String(client.id))) return null;
  const { first, last } = client.firstName != null
    ? { first: String(client.firstName).slice(0, 80), last: String(client.lastName || "").slice(0, 80) }
    : splitClientName(client.name);

  const notesFlat =
    typeof client.notes === "string"
      ? client.notes.slice(0, 8000)
      : Array.isArray(client.notes)
        ? client.notes
            .map((n) => (n && typeof n.text === "string" ? n.text : ""))
            .join("\n")
            .slice(0, 8000) || null
        : null;

  return {
    id: client.id,
    coach_id: coachId,
    first_name: first || "Client",
    last_name: last || "",
    email: client.email || null,
    phone: client.phone || null,
    birth_date: client.birthDate || client.birth_date || null,
    gender: client.gender === "M" || client.gender === "F" || client.gender === "other" ? client.gender : null,
    objective: client.objective ? String(client.objective).slice(0, 2000) : null,
    constraints: client.constraints != null ? String(client.constraints).slice(0, 4000) : null,
    disponibilites: client.disponibilites != null ? String(client.disponibilites).slice(0, 2000) : null,
    preferences: client.preferences != null ? String(client.preferences).slice(0, 4000) : null,
    ai_summary: client.aiSummary != null ? String(client.aiSummary).slice(0, 8000) : null,
    ai_summary_date: client.aiSummaryDate || null,
    notes: notesFlat,
    status: normalizeClientStatus(client.status),
    billing_mode: ["session", "pack", "subscription", "programme"].includes(client.billingMode)
      ? client.billingMode
      : "session",
    payload: entityToPayload(client),
  };
}

function padTime(h, m) {
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}

/** "HH:MM" ou "H:MM" → { start: "HH:MM:SS", end: "HH:MM:SS" } */
export function eventTimeToStartEnd(timeStr, durationMin = 60) {
  const raw = (timeStr || "09:00").trim();
  const m = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return { start: "09:00:00", end: "10:00:00" };
  let h = parseInt(m[1], 10);
  let min = parseInt(m[2], 10);
  const startTotal = h * 60 + min;
  const endTotal = startTotal + Math.max(15, Number(durationMin) || 60);
  const eh = Math.floor(endTotal / 60) % 24;
  const em = endTotal % 60;
  return {
    start: padTime(h, min),
    end: padTime(eh, em),
  };
}

const EVENT_STATUS_DB = new Set(["scheduled", "done", "cancelled", "standby"]);

/**
 * @returns {object|null}
 */
export function eventAppToDbRow(event, coachId) {
  if (!event || !isUuid(String(event.id))) return null;
  const { start, end } = eventTimeToStartEnd(event.time, event.duration);
  let st = event.status === "no_show" ? "cancelled" : event.status;
  st = st && EVENT_STATUS_DB.has(st) ? st : "scheduled";
  const cid = event.clientId != null && isUuid(String(event.clientId)) ? event.clientId : null;
  let recId = null;
  if (event.recurrenceGroupId != null && isUuid(String(event.recurrenceGroupId))) {
    recId = event.recurrenceGroupId;
  }

  let cpId = null;
  if (event.clientProductId != null && isUuid(String(event.clientProductId))) cpId = event.clientProductId;

  return {
    id: event.id,
    coach_id: coachId,
    client_id: cid,
    title: event.sessionTitle || (typeof event.client === "string" ? event.client.slice(0, 200) : null) || null,
    date: event.date,
    start_time: start,
    end_time: end,
    duration: Math.max(15, Number(event.duration) || 60),
    status: st,
    is_recurring: !!(event.recurringGroupId && event.type === "recurring"),
    recurrence_group_id: recId,
    location: event.location || null,
    session_notes: event.sessionNotes || event.session_notes || null,
    ai_summary: event.aiSummary || event.ai_summary || null,
    billable: event.billable !== false,
    billing_mode: ["session", "pack", "subscription", "programme"].includes(event.billingMode)
      ? event.billingMode
      : null,
    client_product_id: cpId,
    workout_id: event.workoutId != null && isUuid(String(event.workoutId)) ? event.workoutId : null,
    payload: entityToPayload(event),
  };
}

/** Produit app → ligne `products` (types SQL : session | pack | subscription | programme) */
export function productAppToDbRow(product, coachId) {
  if (!product || !isUuid(String(product.id))) return null;
  let type = product.type;
  if (type === "program") type = "programme";
  if (!["session", "pack", "subscription", "programme"].includes(type)) return null;

  const row = {
    id: product.id,
    coach_id: coachId,
    name: String(product.name || "Offre").slice(0, 200),
    type,
    unit_price: null,
    pack_size: null,
    pack_unit_price: null,
    subscription_price: null,
    subscription_sessions: null,
    is_active: product.isActive !== false,
  };

  const amt = product.pricing?.amount != null ? Number(product.pricing.amount) : 0;
  if (type === "session") {
    row.unit_price = amt;
  } else if (type === "pack") {
    const n = product.sessions?.count || product.packSize || 10;
    row.pack_size = n;
    row.pack_unit_price = n > 0 ? Math.round((amt / n) * 100) / 100 : amt;
  } else if (type === "subscription") {
    row.subscription_price = amt;
    row.subscription_sessions = product.sessions?.count || 4;
  } else if (type === "programme") {
    const n = product.sessions?.count || 12;
    row.pack_size = n;
    row.pack_unit_price = n > 0 ? Math.round((amt / n) * 100) / 100 : amt;
    row.objective = product.programmeObjective || product.description || null;
    row.duration_weeks = product.durationWeeks ?? null;
    row.includes_assessment = !!product.includesAssessment;
    row.payment_schedule = product.paymentSchedule || "once";
  }

  row.payload = entityToPayload(product);
  return row;
}
